import { useContext, useState, useEffect } from "react";
import Valve from "../components/Valve";
import Transmitter from "../components/Transmitter";
import "./PigLauncher.css";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import { WellContext } from "../context/WellContext";
import { usePig } from "../components/PigEngine";

import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";


/* ================= VALVE OUTPUT ================= */

function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}



function PigLauncher() {

        useEffect(() => {
  
    trackPageVisit("PigLauncher");
  
  }, []);


const [hoveredValve, setHoveredValve] = useState(null);




const tagStyle = {
      position: "absolute",
      top: -30,
      left: "50%",
      transform: "translateX(-50%)",

      background: "black",
      color: "#00ffcc",

      fontSize: "13px",
      fontWeight: "bold",

      padding: "3px 8px",
      borderRadius: "4px",

      border: "1px solid #555",

      whiteSpace: "nowrap",
      zIndex: 999
    };



  const {
    valves,
    toggleValve
  } = useContext(WellContext);

  /* ================= CONTROL VALVE DATA ================= */

  const DEFAULT_VALVES = {

    FCV_1701: {
      mode: "Manual",
      opening: 0,
      sp: "",
      pv: ""
    }

  };

  const [valveData, setValveData] = useState(() => {

    const saved =
      localStorage.getItem("PigLauncher_valveData");

    if (!saved) return DEFAULT_VALVES;

    try {

      const parsed = JSON.parse(saved);

      return {

        FCV_1701: {
          ...DEFAULT_VALVES.FCV_1701,
          ...parsed.FCV_1701
        }

      };

    } catch {

      return DEFAULT_VALVES;
    }
  });

  /* ================= SAVE ================= */

  useEffect(() => {

    localStorage.setItem(
      "PigLauncher_valveData",
      JSON.stringify(valveData)
    );

  }, [valveData]);

  /* ================= UPDATE ================= */

  const updateValveData = (
    tag,
    field,
    value
  ) => {

    setValveData(prev => ({

      ...prev,

      [tag]: {

        ...(prev[tag] || DEFAULT_VALVES[tag]),

        [field]:
          field === "opening"
            ? Number(value) || 0
            : value

      }

    }));
  };

  /* ================= PANEL CONTROL ================= */

  const [openPanels, setOpenPanels] =
    useState([]);

  const openPanel = (tag) => {

    if (!openPanels.includes(tag)) {

      setOpenPanels(prev => [...prev, tag]);
    }
  };

  const closePanel = (tag) => {

    setOpenPanels(prev =>
      prev.filter(p => p !== tag)
    );
  };


// ======================================
// GLOBAL PIG RERENDER
// ======================================

const [, forceRender] = useState(0);

useEffect(() => {

  const renderLoop = setInterval(() => {

    forceRender(v => v + 1);

  }, 30);

  return () => clearInterval(renderLoop);

}, []);




const {
  pigLoaded,
  pigType,
  pigVisible,
  pigX,
  pigY,
  pigPage,
  pigPaused,
  loadPig
} = usePig();




  /* ================= VALVE STATUS ================= */

  const isXV1701Open =
    valves?.XV_1701 === false;

  const isXV1702Open =
    valves?.XV_1702 === false;

  const isXV1703Open =
    valves?.XV_1703 === false;

  const isXV1704Open =
    valves?.XV_1704 === false;

  const isXV1705Open =
    valves?.XV_1705 === false;

  const isXV1706Open =
    valves?.XV_1706 === false;

  const isXV1707Open =
    valves?.XV_1707 === false;

  const isXV1708Open =
    valves?.XV_1708 === false;


    

  /* ================= FLOW LOGIC ================= */

  const FIC_1705 =
    20 *
    (valveData.FCV_1701?.opening || 0) /
    100;

  const FIC_1707 =
    isXV1701Open
      ? FIC_1705
      : 0;

  const FIC_1706 =

    isXV1702Open &&
    isXV1703Open

      ? 520

      : (
          isXV1702Open ||
          isXV1703Open
        )

      ? 300

      : 0;

  const PT_1701 = isXV1702Open || isXV1703Open ? 2.3 : 0.03;

  // 🧠 NEW: realistic pressure drop + delay effect
  const PT_1702 =
    PT_1701 * (1 - FIC_1705 / 1000);

  const TT_1701 = 45 + (FIC_1705 / 20);

  const ZT_1701 = isXV1705Open ? 1 : 0;
  const ZT_1702 = isXV1706Open ? 1 : 0;

  const FIC_1702 = FIC_1706 * 0.85;

  
      useEffect(() => {
  localStorage.setItem("isXV1702Open", isXV1702Open);
  localStorage.setItem("isXV1703Open", isXV1703Open);
  localStorage.setItem("isXV1705Open", isXV1705Open);
  localStorage.setItem("isXV1706Open", isXV1706Open);
  localStorage.setItem("isXV1707Open", isXV1707Open);
  localStorage.setItem("isXV1708Open", isXV1708Open);
  localStorage.setItem("FIC_1706", FIC_1706);
}, [
  isXV1702Open,
  isXV1703Open,
  isXV1705Open,
  isXV1706Open,
  isXV1707Open,
  isXV1708Open,
  FIC_1706
]);




  return (

    <div className="PigLauncher-container">

      {/* ================= TITLE ================= */}

      <h2
        style={{
          position: "absolute",
          top: "-18px",
          left: "252px",
          color: "white",
          background: "rgb(30, 5, 250)",
          padding: "2.2px 331px"
        }}
      >
        PIG LAUNCHER
      </h2>

      {/* ================= LOAD RECTANGLE PIG ================= */}

      <div
        onClick={() => loadPig("rect")}
        onDoubleClick={() => loadPig("circle")}
        title="Single Click = Rectangle Pig | Double Click = Circle Pig"
        style={{
          position: "absolute",
          left: 20,
          top: 230,
          padding: "10px 20px",
          background: "#222",
          color: "white",
          cursor: "pointer",
          border: "2px solid cyan",
          borderRadius: "6px",
          zIndex: 5000,
          userSelect: "none",
          transition: "0.3s"
        }}
      >
        Load Pig
      </div>

      {/* ================= PIG SVG ================= */}

      {
        pigVisible &&
        pigPage === "launcher" && (

          <svg
            style={{
              position: "absolute",
              left: pigX,
              top: pigY,
              overflow: "visible",
              pointerEvents: "none",
              transition:
                "left 0.03s linear, top 0.03s linear",
              zIndex: 4000
            }}
            width={
              pigType === "rect"
                ? 60
                : 35
            }
            height={
              pigType === "rect"
                ? 30
                : 35
            }
          >

            {
              pigType === "rect" ? (

                <rect
                  x="0"
                  y="0"
                  rx="10"
                  ry="10"
                  width="60"
                  height="30"
                  fill="red"
                  stroke="lime"
                  strokeWidth="2"
                />

              ) : (

                <circle
                  cx="17"
                  cy="17"
                  r="10"
                  fill="yellow"
                  stroke="black"
                  strokeWidth="2"
                />

              )
            }

          </svg>
        )
      }

      {/* ================= SHUTDOWN VALVES ================= */}

      <Valve
        tag="XV_1701"
        x={928}
        y={468}
        isClosed={valves.XV_1701}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1702"
        x={610}
        y={324}
        r={90}
        isClosed={valves.XV_1702}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1703"
        x={347}
        y={333}
        r={270}
        isClosed={valves.XV_1703}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1704"
        x={468}
        y={140}
        isClosed={valves.XV_1704}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1705"
        x={685}
        y={244}
        isClosed={valves.XV_1705}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1706"
        x={1058}
        y={93}
        isClosed={valves.XV_1706}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1707"
        x={1046}
        y={300}
        r={90}
        isClosed={valves.XV_1707}
        toggle={toggleValve}
      />

      <Valve
        tag="XV_1708"
        x={387}
        y={428}
        isClosed={valves.XV_1708}
        toggle={toggleValve}
      />

      {/* ================= CONTROL VALVES SVG ================= */}
        

         {/* FCV-1701 */}
        <div
            style={{ position: "absolute", left: 155, top: 55}}
            onMouseEnter={() => setHoveredValve("FCV_1701")}
            onMouseLeave={() => setHoveredValve(null)}
            onClick={() => openPanel("FCV_1701")}
          >

            {hoveredValve === "FCV_1701" && (
              <div style={tagStyle}>
                FCV_1701
              </div>
            )}

            <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-1701">
                    
          <defs>
            <linearGradient id="metal_1701">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>


          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z"
            fill="#eb2a11" stroke="#3498db" strokeWidth="2" />

          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />

          <polygon points="20,200 100,150 20,110"
            fill="url(#metal_1701)" stroke="#7f8c8d" strokeWidth="2" />

          <polygon points="180,200 100,150 180,110"
            fill="url(#metal_1701)" stroke="#7f8c8d" strokeWidth="2" />

        </svg>
      </div>
      
     

      {/* ================= FACEPLATES ================= */}

      {
        openPanels.map((tag, index) => (

          <ControlValveControlPanel
            key={tag}
            tag={tag}
            data={valveData[tag]}
            update={updateValveData}
            close={() => closePanel(tag)}
            defaultPosition={{
              x: 300 + index * 40,
              y: 150 + index * 40
            }}
          />

        ))
      }

      {/* ================= OUTPUT ================= */}

      <ValveOutput
        value={valveData.FCV_1701?.opening}
        x={178}
        y={102}
      />

      {/* ================= TRANSMITTERS ================= */}

      <Transmitter
        tag="FIC-1705"
        value={[FIC_1705]}
        x={282}
        y={50}
        unit="SCF/S"
        max={35}
        HH={30}
        H={25}
        L={5}
        LL={3}
        c="lime"
        description="FLOWRATE OF NITROGEN TO PIG LAUNCHER FOR PURGING"
      />

      <Transmitter
        tag="FIC-1707"
        value={[FIC_1707]}
        x={1036}
        y={457}
        unit="SCF/S"
        max={35}
        HH={30}
        H={25}
        L={5}
        LL={3}
        c="lime"
        description="FLOWRATE OF NITROGEN AND FLUIDS/IMPURITIES TO CLOSE DRAIN AFTER PURGING OF PIG LAUNCHER BARREL"
      />

      <Transmitter
        tag="FIC-1706"
        value={[FIC_1706]}
        x={168}
        y={350}
        unit="SCF/S"
        max={600}
        HH={500}
        H={400}
        L={50}
        LL={20}
        c="lime"
        description="FLOWRATE OF FROM GAS LIFT MANIFOLD/HEADER TO PIG LAUNCHER TO LAUNCH THE PIG FOR PIGGING"
      />

      <Transmitter
        tag="PT-1701"
        value={[PT_1701]}
        x={188}
        y={217}
        unit="MPa"
        max={3}
        HH={2.5}
        H={2}
        L={0.4}
        LL={0.01}
        c="lime"
        description="PRESSURE OF PIG LAUNCHER BARREL DURING PIGGING"
      />



      <Transmitter tag="FIC-1702" value={[FIC_1702]} x={915} y={217} unit="SCF/S" max={350} HH={150} H={250} L={50} LL={20} c="lime" description="FLOWRATE OF PIG LAUNCHER MAIN LINE DURING PIGGING"/>

      <Transmitter tag="PT-1702" value={[PT_1702]} x={734} y={217} unit="MPa" max={3} HH={2.5} H={2} L={0.4} LL={0.04} c="lime" description="PRESSURE OF PIG LAUNCHER MAIN LINE DURING PIGGING"/>

      <Transmitter tag="TT-1701" value={[TT_1701]} x={820} y={217} unit="°C" max={120} HH={80} H={60} L={20} LL={10} c="lime" description="TEMPERATURE OF PIG LAUNCHER MAIN LINE DURING PIGGING"/>


    </div>
  );
}

export default PigLauncher;